import { useEffect, useState } from "react";
import { Loader2, X, Download, ExternalLink, FileText, Image as ImageIcon, File } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { getAdminToken } from "../../../lib/admin";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Worker is shared with the Pricing Book viewer.
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic"];
const PDF_EXTS = ["pdf"];

const extOf = (fn) => (fn || "").split(".").pop().toLowerCase();

const isImage = (fn) => IMAGE_EXTS.includes(extOf(fn));
const isPdf = (fn) => PDF_EXTS.includes(extOf(fn));

/**
 * Fetch an admin-protected attachment as a Blob URL.
 * The /api routes require the x-admin-password header so we can't simply
 * point an <img>/<iframe>/etc at the URL — we proxy through fetch + blob.
 */
const fetchAuthedBlobUrl = async (url) => {
  const res = await fetch(url, { headers: { "x-admin-password": getAdminToken() || "" } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

/**
 * Tiny inline thumbnail. Renders as a 64px square button. Images get a real
 * preview; PDFs render the first page; other types show a generic icon.
 */
export const AttachmentThumb = ({ url, filename, onOpen }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  const ext = extOf(filename);

  useEffect(() => {
    let revoked = false;
    let theBlob = null;
    if (!isImage(filename)) return undefined;
    fetchAuthedBlobUrl(url)
      .then((b) => {
        if (revoked) URL.revokeObjectURL(b);
        else { theBlob = b; setBlobUrl(b); }
      })
      .catch(() => setErr(true));
    return () => {
      revoked = true;
      if (theBlob) URL.revokeObjectURL(theBlob);
    };
  }, [url, filename]);

  return (
    <button
      type="button"
      onClick={() => onOpen({ url, filename })}
      className="group flex flex-col items-stretch overflow-hidden rounded-sm transition hover:shadow-md"
      style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)", maxWidth: 160 }}
      data-testid={`attachment-thumb-${filename}`}
      title={`Preview ${filename}`}
    >
      <div
        className="relative flex items-center justify-center"
        style={{ width: "100%", height: 72, background: "#fff" }}
      >
        {isImage(filename) ? (
          blobUrl ? (
            <img src={blobUrl} alt={filename} className="max-w-full max-h-full object-contain" />
          ) : err ? (
            <ImageIcon size={20} className="text-stone-400" />
          ) : (
            <Loader2 size={14} className="animate-spin text-stone-400" />
          )
        ) : isPdf(filename) ? (
          <FileText size={22} className="text-red-700" />
        ) : (
          <File size={22} className="text-stone-500" />
        )}
      </div>
      <div className="px-1.5 py-1 text-[10px] font-mono leading-tight truncate" style={{ borderTop: "1px solid var(--ups-border)" }}>
        <span className="font-bold uppercase">{ext || "file"}</span>
        <span className="ml-1 text-[color:var(--ups-subtext)] truncate">{filename}</span>
      </div>
    </button>
  );
};

/**
 * Full-screen attachment viewer. Supports image zoom and PDF paging.
 */
const AttachmentPreview = ({ attachment, onClose }) => {
  const { url, filename } = attachment || {};
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);

  useEffect(() => {
    if (!url) return undefined;
    let active = true;
    let theBlob = null;
    setBlobUrl(null);
    setErr(null);
    setPdfPage(1);
    setPdfNumPages(0);
    fetchAuthedBlobUrl(url)
      .then((b) => {
        if (!active) { URL.revokeObjectURL(b); return; }
        theBlob = b;
        setBlobUrl(b);
      })
      .catch((e) => { if (active) setErr(e?.message || "Could not load"); });
    return () => {
      active = false;
      if (theBlob) URL.revokeObjectURL(theBlob);
    };
  }, [url]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && isPdf(filename)) setPdfPage((p) => Math.min(pdfNumPages, p + 1));
      else if (e.key === "ArrowLeft" && isPdf(filename)) setPdfPage((p) => Math.max(1, p - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, filename, pdfNumPages]);

  if (!attachment) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col"
      style={{ background: "rgba(28,25,23,0.94)" }}
      data-testid="attachment-preview-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex items-center justify-between px-4 sm:px-6 py-3 border-b"
        style={{ background: "#1C1917", borderColor: "rgba(255,181,0,0.3)" }}
      >
        <div className="flex items-center gap-3 min-w-0 text-white">
          <div
            className="px-2 py-1 text-[10px] font-bold tracking-[0.18em] uppercase"
            style={{ background: "#FFB500", color: "#1C1917" }}
          >
            {(extOf(filename) || "file").toUpperCase()}
          </div>
          <div className="font-display text-sm sm:text-base font-bold truncate">{filename}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {blobUrl && (
            <a
              href={blobUrl}
              download={filename}
              className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white hover:bg-white/10 rounded-sm flex items-center gap-1.5"
              title="Download"
              data-testid="attachment-preview-download"
            >
              <Download size={13} /> <span className="hidden sm:inline">Download</span>
            </a>
          )}
          {blobUrl && !isImage(filename) && !isPdf(filename) && (
            <a
              href={blobUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white hover:bg-white/10 rounded-sm flex items-center gap-1.5"
              title="Open in new tab"
            >
              <ExternalLink size={13} /> Open
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-white hover:bg-white/10 rounded-sm"
            title="Close (Esc)"
            data-testid="attachment-preview-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-4 overflow-auto relative">
        {!blobUrl && !err && (
          <div className="text-white flex flex-col items-center gap-2">
            <Loader2 size={20} className="animate-spin" />
            <div className="text-xs font-mono opacity-75">Loading…</div>
          </div>
        )}
        {err && (
          <div className="text-white text-sm">
            <div className="font-bold mb-1">Couldn't load attachment.</div>
            <div className="opacity-75">{err}</div>
          </div>
        )}
        {blobUrl && isImage(filename) && (
          <img
            src={blobUrl}
            alt={filename}
            className="max-w-full max-h-full object-contain bg-white"
            style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.2)" }}
            data-testid="attachment-preview-image"
          />
        )}
        {blobUrl && isPdf(filename) && (
          <Document
            file={blobUrl}
            onLoadSuccess={(pdf) => setPdfNumPages(pdf.numPages)}
            loading={<Loader2 size={20} className="animate-spin text-white" />}
            error={<div className="text-white text-sm">Could not render PDF.</div>}
            data-testid="attachment-preview-pdf"
          >
            <Page
              pageNumber={pdfPage}
              width={Math.min(window.innerWidth - 80, 1100)}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              loading={<Loader2 size={20} className="animate-spin text-white" />}
            />
          </Document>
        )}
        {blobUrl && !isImage(filename) && !isPdf(filename) && (
          <div className="text-white text-center">
            <div className="font-bold mb-2">Preview not available for this file type.</div>
            <a
              href={blobUrl}
              download={filename}
              className="underline text-amber-300 text-sm"
            >
              Click to download
            </a>
          </div>
        )}
      </div>

      {isPdf(filename) && pdfNumPages > 0 && (
        <div
          className="flex items-center justify-center gap-4 px-4 py-3 border-t text-white"
          style={{ background: "#1C1917", borderColor: "rgba(255,181,0,0.3)" }}
        >
          <button
            type="button"
            onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
            disabled={pdfPage <= 1}
            className="px-3 py-1 text-[11px] rounded-sm disabled:opacity-30 hover:bg-white/10"
          >
            ← Prev
          </button>
          <div className="text-[11px] font-mono" data-testid="attachment-preview-pdf-page">
            Page {pdfPage} of {pdfNumPages}
          </div>
          <button
            type="button"
            onClick={() => setPdfPage((p) => Math.min(pdfNumPages, p + 1))}
            disabled={pdfPage >= pdfNumPages}
            className="px-3 py-1 text-[11px] rounded-sm disabled:opacity-30 hover:bg-white/10"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AttachmentPreview;
